import { getSocket } from '@/services/socket/websocket.service.js'

// Keep track of registered event names to properly clean them up for calls
const callRegisteredEventNames = new Set()
let callStoreInstance = null // To store the call store instance
let authStoreInstance = null // To store the auth store instance

/**
 * Sets up the socket listeners for call-related events.
 * @param {Object} callStore - The call store instance.
 * @param {Object} authStore - The auth store instance.
 */
export function setupCallSocketListeners(callStore, authStore) {
    const socket = getSocket()
    if (!socket) {
        console.error(
            'Cannot setup call socket listeners: Socket not available.'
        )
        return
    }

    callStoreInstance = callStore // Store the instance for use in handlers
    authStoreInstance = authStore // Store the instance for use in handlers

    console.log('Setting up call socket listeners...')

    // Define handlers here or import them if they become complex
    const onIncomingDial = (data) => {
        console.log('Socket event received: incoming_call', data)
        if (callStoreInstance) {
            callStoreInstance.handleIncomingDial(data)
        }
    }

    const onCallRejected = (data) => {
        console.log('Socket event received: call_rejected', data)
        if (callStoreInstance) {
            callStoreInstance.handleCallRejected(data)
        }
    }

    const onCallCancelled = (data) => {
        console.log('Socket event received: call_cancelled', data)
        if (callStoreInstance) {
            callStoreInstance.handleCallCancelled(data)
        }
    }

    const onCallReady = (data) => {
        const currentSocketId = socket?.id
        console.log('=== [callSocket] RECEIVED: call_ready ===')
        console.log('[callSocket] My socket ID:', currentSocketId)
        console.log('[callSocket] call_ready data:', data)
        console.log(
            '[callSocket] isReceivingCall:',
            callStoreInstance?.state?.isReceivingCall
        )
        console.log(
            '[callSocket] isDialInProgress:',
            callStoreInstance?.state?.isDialInProgress
        )
        console.log(
            '[callSocket] isCallInProgress:',
            callStoreInstance?.state?.isCallInProgress
        )

        if (!callStoreInstance) {
            console.error(
                '[callSocket] Call store instance not available for call_ready event'
            )
            return
        }

        // IMPORTANT: Only handle call_ready if THIS device was involved in the call
        // Either as the one dialing (client: isDialInProgress) or accepting (creator: isReceivingCall)
        // Also allow if it's a reconnection (data.isReconnection)
        const isDialing = callStoreInstance.state.isDialInProgress
        const isReceiving = callStoreInstance.state.isReceivingCall
        const isReconnection = data?.isReconnection === true

        if (!isDialing && !isReceiving && !isReconnection) {
            console.warn(
                '[callSocket] IGNORING call_ready - this device was not involved in the call.',
                'isDialing:',
                isDialing,
                'isReceiving:',
                isReceiving,
                'isReconnection:',
                isReconnection
            )
            return
        }

        try {
            // Preserve/restore call type on refresh/reconnection
            if (data?.callType === 'video' || data?.callType === 'voice') {
                callStoreInstance.state.currentCallType = data.callType
            }
            // Add local receive time for clock skew calculation in Call.vue
            const payload = { ...data, clientReceiveTime: Date.now() }
            callStoreInstance.handleCallReady(payload)
            console.log(
                '[callSocket] handleCallReady completed, isCallInProgress:',
                callStoreInstance.state.isCallInProgress
            )
        } catch (e) {
            console.error('[callSocket] Error handling call_ready:', e)
            // Do not re-call handleCallReady - just log the error
        }
    }

    const onCallEnded = (data) => {
        console.log('Socket event received: call_ended', data)
        if (callStoreInstance) {
            callStoreInstance.handleCallEnded(data)
        }
    }

    const onParticipantDisconnected = (data) => {
        console.log('Socket event received: participant_disconnected', data)
        if (callStoreInstance) {
            callStoreInstance.handleParticipantDisconnected(data)
        }
    }

    const onParticipantReconnected = (data) => {
        console.log('Socket event received: participant_reconnected', data)
        if (callStoreInstance) {
            callStoreInstance.handleParticipantReconnected(data)
        }
    }

    const onBalanceUpdated = (data) => {
        console.log('Socket event received: balance_updated', data)
        if (authStoreInstance) {
            console.log('Refreshing user balance due to balance_updated event.')
            authStoreInstance.refreshUserBalance()
        }
    }

    // Handler for when the call was accepted on another device
    const onCallAcceptedElsewhere = (data) => {
        const currentSocketId = socket?.id
        console.log('=== [callSocket] RECEIVED: call_accepted_elsewhere ===')
        console.log('[callSocket] My socket ID:', currentSocketId)
        console.log(
            '[callSocket] Accepted on socket ID:',
            data?.acceptedOnSocketId
        )
        console.log('[callSocket] call_accepted_elsewhere data:', data)

        if (callStoreInstance) {
            callStoreInstance.handleCallAcceptedElsewhere(data)
            console.log(
                '[callSocket] handleCallAcceptedElsewhere completed, isReceivingCall:',
                callStoreInstance.state.isReceivingCall
            )
        }
    }

    const eventsToRegister = {
        incoming_call: onIncomingDial,
        call_rejected: onCallRejected,
        call_cancelled: onCallCancelled,
        call_ready: onCallReady,
        call_ended: onCallEnded,
        participant_disconnected: onParticipantDisconnected,
        participant_reconnected: onParticipantReconnected,
        balance_updated: onBalanceUpdated,
        call_accepted_elsewhere: onCallAcceptedElsewhere,
    }

    for (const [eventName, handler] of Object.entries(eventsToRegister)) {
        if (handler && typeof handler === 'function') {
            if (!callRegisteredEventNames.has(eventName)) {
                socket.on(eventName, handler)
                callRegisteredEventNames.add(eventName)
            }
        }
    }
}

/**
 * Sends a heartbeat to the server to indicate that the user is still in an active call
 * @param {String} sessionId - The ID of the active call session
 * @returns {Boolean} - Whether the heartbeat was sent successfully
 */
export function sendCallInProgressHeartbeat(sessionId) {
    const socket = getSocket()
    if (!socket || !sessionId) {
        console.error(
            'Cannot send call_inprogress: Socket not available or sessionId missing'
        )
        return false
    }

    console.log('Sending call_inprogress heartbeat for session:', sessionId)
    socket.emit('call_inprogress', { sessionId })
    return true
}

/**
 * Cleans up all call-specific socket listeners.
 */
export function cleanupCallSockets() {
    const socket = getSocket()
    if (socket) {
        console.log(
            'Cleaning up call socket listeners for events:',
            Array.from(callRegisteredEventNames)
        )
        callRegisteredEventNames.forEach((eventName) => {
            socket.off(eventName) // Removes all listeners for this event name that were added by this service
            console.log(`Removed call listeners for ${eventName}`)
        })
    }
    callRegisteredEventNames.clear()
    callStoreInstance = null // Clear the stored instance
    authStoreInstance = null // Clear the stored instance
    console.log('Call socket listener tracking cleared.')
}

// Export a default object if preferred for consistency with other services
export default {
    setupCallSocketListeners,
    cleanupCallSockets,
    sendCallInProgressHeartbeat,
}
